import type { PlatformSupportConversationFieldsFragment as Conversation } from '@web-app/generated/platform/graphql';
import { plural } from '@web-app/utils/support-concierge/conversation-view';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/** The latest support message of each conversation; a change is a new message. */
export function latestSupportMessages(
  conversations: Conversation[],
): Map<string, string> {
  return new Map(
    conversations.flatMap((conversation) => {
      const last = conversation.messages.findLast(
        (message) => !message.fromProvider,
      );
      return last ? [[conversation.id, last.id] as const] : [];
    }),
  );
}

/**
 * Pops up when a support message arrives, however the unread list learned of
 * it (event or refetch). Each viewer's first loaded list is the baseline, so
 * reloads and account switches stay quiet; `muted` keeps tracking without
 * showing anything.
 */
export function useSupportNewMessageToast({
  viewerId,
  conversations,
  loaded,
  muted,
  onView,
}: {
  viewerId: string;
  conversations: Conversation[];
  loaded: boolean;
  muted: boolean;
  onView: (conversationId?: string) => void;
}) {
  // Every conversation this viewer has had unread keeps its last seen support
  // message, so one that drops out (read) and is listed again by lagging search
  // with the same message is not news.
  const seen = useRef<{
    viewerId: string;
    known: Map<string, string>;
  } | null>(null);

  // No deps: comparing message ids after each render is cheap and idempotent.
  useEffect(function toastNewSupportMessages() {
    if (!loaded) return;
    const latest = latestSupportMessages(conversations);
    const known =
      seen.current?.viewerId === viewerId ? seen.current.known : null;
    seen.current = { viewerId, known: new Map([...(known ?? []), ...latest]) };
    if (!known || muted) return;
    const arrived = conversations.filter(
      ({ id }) => latest.has(id) && latest.get(id) !== known.get(id),
    );
    if (arrived.length === 0) return;

    const [only] = arrived;
    const single = arrived.length === 1 ? only : undefined;
    toast.success(
      single
        ? 'New message from Berry Street Support'
        : `${plural(arrived.length, 'new message')} from Berry Street Support`,
      {
        description: single?.title,
        action: { label: 'View', onClick: () => onView(single?.id) },
      },
    );
  });
}
